import React, { useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  User,
  Zap,
  ShoppingBag,
  Users,
  Building2,
  Clock,
  Settings,
  BarChart3,
  Calendar,
  Headphones,
  Star,
  Database,
  MessageSquareText,
  Search,
  FileText,
  MousePointer,
  CheckCheck,
} from "lucide-react";
import Footer from "../components/Footer";
import SEO from "../components/SEO";

// =========================================================================
// SEO CONFIGURATION (Configurable metadata, OpenGraph, Twitter & Schema)
// =========================================================================
export const CITIZEN_SERVICES_SEO = {
  title: "Citizen Services Assistant | Government & Public Sector AI | Simpel",
  description:
    "Simpel Citizen Services Assistant helps citizens find relevant information, understand public schemes, and access government services easily with guided AI self-service.",
  keywords: [
    "Citizen Services Assistant",
    "Public Sector AI",
    "Government AI Assistant",
    "Citizen Portal AI",
    "Government Public Services",
    "Citizen Self Service",
    "Simpel Cognitive AI",
    "Public Sector Automation",
    "Scheme Eligibility AI",
    "Citizen Journey Automation",
  ],
  canonical: "/cognitive/industries/government-public-services/citizen-services-assistant",
  ogType: "website",
  ogTitle: "Citizen Services Assistant: Public services that citizens can actually navigate",
  ogDescription:
    "Discover how Simpel Citizen Services Assistant helps public sector agencies reduce counter volumes by 20–35% and boost self-service completion by 25–45%.",
  ogImage: "/images/insights/citizen-services-hero.jpg",
  twitterCard: "summary_large_image",
  twitterTitle: "Citizen Services Assistant | Simpel Cognitive AI",
  twitterDescription:
    "Make public services simpler, faster, and more accessible with Simpel Citizen Services Assistant.",
  robots: "index, follow",
  schema: {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "Simpel Citizen Services Assistant",
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web",
    description:
      "An AI assistant that helps citizens easily find information, understand eligibility, prepare documents, and complete government service journeys.",
    brand: {
      "@type": "Brand",
      name: "Simpel",
    },
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
    },
    audience: {
      "@type": "Audience",
      audienceType: "Public Sector Agencies, Government Departments, Citizens",
    },
  },
};

const CASE_ROW_1 = [
  {
    icon: Building2,
    title: "Industry",
    desc: "Public Sector (Citizen Services)",
  },
  {
    icon: Users,
    title: "Key Users",
    desc: "Citizens, Front-line Staff, Programme Managers, Digital Services Teams",
  },
  {
    icon: Clock,
    title: "Business Need",
    desc: "Make public services easier to find, understand and access",
  },
];

const CASE_ROW_2 = [
  {
    icon: Settings,
    title: "Solution",
    desc: "Simpel Citizen Services Assistant",
  },
  {
    icon: BarChart3,
    title: "Impact",
    desc: "25–45% increase in self-service",
  },
  {
    icon: Calendar,
    title: "Timeframe",
    desc: "10–16 months payback",
  },
];

const WHO_BENEFITS_DATA = [
  {
    role: "Head of Citizen Services / Digital Services",
    improves:
      "Higher self-service completion and clearer citizen journeys. More routine queries resolved without escalation; satisfaction with information access improves on the pilot scope.",
  },
  {
    role: "Department / Programme Manager",
    improves:
      "Fewer repeated questions about the same processes. Official guidance is used more consistently; citizens reach the right step faster.",
  },
  {
    role: "Front-line / Contact Centre Staff",
    improves:
      'More capacity for complex cases. Routine "where do I start?" and "what do I need?" questions are handled by the Assistant; staff focus on exceptions.',
  },
  {
    role: "Communications / Content Lead",
    improves:
      "Approved content is actively used. Schemes, process pages and FAQs stay visible at the point of need, not only on static websites.",
  },
  {
    role: "IT / Digital / Information Security",
    improves:
      "Controlled integration with official content sources. Access, audit and privacy requirements met by design.",
  },
];

const RESULTS_DATA = [
  {
    icon: Users,
    subtitle: "Share of routine queries resolved via self-service (pilot services)",
    title: "25% to 45% increase",
  },
  {
    icon: Clock,
    subtitle: "Time for citizens to find relevant process / service information",
    title: "40% to 60% faster",
  },
  {
    icon: Headphones,
    subtitle: 'Contact centre / counter volume for "how do I...?" questions (pilot scope)',
    title: "20% to 35% reduction",
  },
  {
    icon: Star,
    subtitle: "Citizen satisfaction with ease of finding information (pilot feedback)",
    title: "Clear improvement",
  },
  {
    icon: Calendar,
    subtitle: "Typical payback window",
    title: "10 – 16 months",
  },
  {
    icon: Database,
    subtitle: "Annual value (self-service + staff capacity)",
    title: "$150k – $400k (scale-dependent)",
  },
];

const PROCESS_STEPS_CITIZEN = [
  {
    id: "ask",
    title: "Ask",
    desc: "Citizen asks a question in plain language.",
    icon: MessageSquareText,
    circleClass:
      "bg-[#E0F2FE] text-[#0284C7] shadow-[0_0_24px_rgba(2,132,199,0.3)] border border-[#BAE6FD]",
  },
  {
    id: "find",
    title: "Find",
    desc: "The Assistant searches approved public content.",
    icon: Search,
    circleClass:
      "bg-[#F3E8FF] text-[#9333EA] shadow-[0_0_24px_rgba(147,51,234,0.3)] border border-[#E9D5FF]",
  },
  {
    id: "guide",
    title: "Guide",
    desc: "Provides clear steps, required documents and forms.",
    icon: FileText,
    circleClass:
      "bg-[#FEF9C3] text-[#854D0E] shadow-[0_0_24px_rgba(202,138,4,0.3)] border border-[#FEF08A]",
  },
  {
    id: "access",
    title: "Access",
    desc: "Helps the citizen start or complete the service.",
    icon: MousePointer,
    circleClass:
      "bg-[#DCFCE7] text-[#16A34A] shadow-[0_0_24px_rgba(22,163,74,0.3)] border border-[#BBF7D0]",
  },
  {
    id: "resolve",
    title: "Resolve",
    desc: "More queries are completed without a call or counter visit.",
    icon: CheckCheck,
    circleClass:
      "bg-[#FCE7F3] text-[#DB2777] shadow-[0_0_24px_rgba(219,39,119,0.3)] border border-[#FBCFE8]",
  },
];

const CitizenServicesAssistant = ({ seo = CITIZEN_SERVICES_SEO }) => {
  const navigate = useNavigate();

  // Scroll to top on page mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleBack = () => {
    if (window.history.length > 2) {
      navigate(-1);
    } else {
      navigate("/ai-opportunities?tab=Industries");
    }
  };

  const containerRef = useRef(null);

  // Scroll tracking across sticky hero track (matching other pages)
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"],
  });

  const overlayY = useTransform(
    scrollYProgress,
    [0, 0.22, 0.75, 0.98],
    ["100%", "0%", "0%", "-100%"]
  );

  const overlayOpacity = useTransform(
    scrollYProgress,
    [0, 0.06, 0.22, 0.75, 0.95, 1],
    [0, 0.6, 1, 1, 0.7, 0]
  );

  const handleScrollDown = () => {
    const el = document.getElementById("case-at-a-glance");
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    } else if (containerRef.current) {
      const top = containerRef.current.offsetTop;
      const height = containerRef.current.offsetHeight;
      window.scrollTo({
        top: top + height,
        behavior: "smooth",
      });
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 text-slate-100 flex flex-col font-sans selection:bg-[#00D2EE] selection:text-black">
      {/* Configurable Dynamic SEO (Title, Meta, OG, Twitter & JSON-LD Schema) */}
      <SEO {...seo} />

      {/* Top Floating App Bar */}
      <header className="sticky top-0 z-50 w-full border-b border-white/10 bg-[#000000]/80 backdrop-blur-md">
        <div className="mx-auto flex h-[76px] sm:h-[82px] max-w-[1440px] items-center justify-between px-6 lg:px-[70px]">
          {/* Left Brand Identity */}
          <Link
            to="/"
            className="flex items-center gap-3 sm:gap-3.5 group select-none shrink-0"
          >
            <img
              src="/images/simpel-icon-mark.png"
              alt="Simpel"
              className="h-[34px] sm:h-[38px] w-auto object-contain transition-transform duration-300 group-hover:scale-105"
            />
            <div className="flex flex-col justify-center">
              <span className="text-[22px] sm:text-[25px] font-bold leading-none tracking-tight text-white">
                Simpel
              </span>
              <span className="mt-[3px] text-[7.5px] sm:text-[8px] font-bold uppercase tracking-[2.2px] sm:tracking-[2.5px] text-white/70">
                AUTOMATION SIMPLIFIED
              </span>
            </div>
          </Link>

          {/* Right Breadcrumb & Back Button */}
          <div className="flex items-center gap-5 sm:gap-8">
            <div className="hidden sm:block border-b-2 border-[#2F6DF6] pb-0.5">
              <span className="text-[14px] sm:text-[15px] font-medium text-[#2F6DF6] tracking-wide select-none">
                Industries / Government & Public Services
              </span>
            </div>

            <button
              onClick={handleBack}
              className="inline-flex items-center gap-2 rounded-full bg-[#00D2EE] px-5 py-2 sm:px-6 sm:py-2.5 text-[13px] sm:text-[14px] font-bold text-black transition-all duration-200 hover:brightness-105 hover:shadow-[0_4px_16px_rgba(0,210,238,0.35)] active:scale-95 cursor-pointer"
            >
              <ArrowLeft className="h-4 w-4 stroke-[2.5]" />
              <span>Back to Cognitive AI</span>
            </button>
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="flex-1 w-full bg-[#000000]">
        {/* =========================================================================
            Hero Section with Scroll Pinning Animation (matching Screenshot 1)
            Background: /images/insights/citizen-services-hero.jpg
           ========================================================================= */}
        <section ref={containerRef} className="relative w-full h-[270vh]">
          {/* Sticky Viewport Stage */}
          <div className="sticky top-[76px] sm:top-[82px] h-[calc(100vh-76px)] sm:h-[calc(100vh-82px)] w-full overflow-hidden bg-[#000000]">
            {/* Underlying Hero Section Photo - 100% crystal clear at scroll 0 */}
            <div className="absolute inset-0 w-full h-full">
              <img
                src="/images/insights/citizen-services-hero.jpg"
                alt="Citizen Services Assistant - Government & Public Services"
                className="w-full h-full object-cover object-center block select-none"
              />
            </div>

            {/* Frosted Blur Overlay Section with Pinning Animation */}
            <motion.div
              style={{
                y: overlayY,
                opacity: overlayOpacity,
              }}
              className="absolute inset-0 z-20 flex flex-col items-center justify-center px-6 sm:px-12 lg:px-20 text-center"
            >
              {/* Frosted Glass Background Tint */}
              <div
                className="absolute inset-0 w-full h-full pointer-events-none"
                style={{
                  backgroundColor: "rgba(15, 27, 41, 0.94)",
                  backdropFilter: "blur(20px)",
                  WebkitBackdropFilter: "blur(20px)",
                }}
              />

              {/* Foreground Content */}
              <div className="relative z-10 max-w-[1240px] flex flex-col items-center pt-4 sm:pt-0">
                {/* Category Tag matching Screenshot 1 */}
                <p className="text-[11px] sm:text-[12px] font-bold uppercase tracking-[3px] text-white/70 mb-5 sm:mb-6">
                  PUBLIC SECTOR - CITIZEN SERVICES
                </p>

                {/* Main Heading matching Screenshot 1 */}
                <h1 className="text-[28px] sm:text-[36px] md:text-[44px] lg:text-[48px] xl:text-[54px] font-bold leading-[1.2] sm:leading-[1.18] text-white tracking-tight">
                  <span className="block whitespace-normal sm:whitespace-nowrap">
                    Citizen Services Assistant:{" "}
                    <span className="text-[#3B82F6]">
                      Public services
                    </span>
                  </span>
                  <span className="block mt-1 sm:mt-1.5 text-[#00D2EE]">
                    that citizens can actually navigate
                  </span>
                </h1>

                {/* Subtitle matching Screenshot 1 (exact 3 lines) */}
                <div className="mt-5 sm:mt-6 max-w-[820px] text-[13.5px] sm:text-[14.5px] md:text-[15px] leading-[24px] sm:leading-[26px] text-white/80 text-center">
                  <p>
                    How Simpel Citizen Services Assistant help citizens find relevant information,
                    <br className="hidden sm:inline" />
                    {" "}understand processes and access services more easily —
                    <br className="hidden sm:inline" />
                    {" "}so public services feel clear, not complicated.
                  </p>
                </div>

                {/* 4 Feature Items in a horizontal row matching Screenshot 1 */}
                <div className="mt-8 sm:mt-10 flex flex-wrap lg:flex-nowrap items-center justify-center gap-4 sm:gap-6 lg:gap-8 w-full max-w-[1200px]">
                  {/* Item 1 */}
                  <div className="flex items-center gap-3 shrink-0">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#00D2EE]/20 text-[#00D2EE] border border-[#00D2EE]/30">
                      <User className="h-4 w-4 stroke-[2.2]" />
                    </div>
                    <span className="text-[12px] sm:text-[13px] font-medium text-slate-200 text-left leading-tight whitespace-nowrap">
                      Clear, trusted<br className="hidden sm:inline" /> information
                    </span>
                  </div>

                  <div className="hidden sm:block h-6 w-[1px] bg-white/20 shrink-0" />

                  {/* Item 2 */}
                  <div className="flex items-center gap-3 shrink-0">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#00D2EE]/20 text-[#00D2EE] border border-[#00D2EE]/30">
                      <Zap className="h-4 w-4 stroke-[2.2]" />
                    </div>
                    <span className="text-[12px] sm:text-[13px] font-medium text-slate-200 text-left leading-tight whitespace-nowrap">
                      Simpler service journeys
                    </span>
                  </div>

                  <div className="hidden sm:block h-6 w-[1px] bg-white/20 shrink-0" />

                  {/* Item 3 */}
                  <div className="flex items-center gap-3 shrink-0">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#00D2EE]/20 text-[#00D2EE] border border-[#00D2EE]/30">
                      <ShoppingBag className="h-4 w-4 stroke-[2.2]" />
                    </div>
                    <span className="text-[12px] sm:text-[13px] font-medium text-slate-200 text-left leading-tight whitespace-nowrap">
                      Higher self-service
                    </span>
                  </div>

                  <div className="hidden sm:block h-6 w-[1px] bg-white/20 shrink-0" />

                  {/* Item 4 */}
                  <div className="flex items-center gap-3 shrink-0">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#00D2EE]/20 text-[#00D2EE] border border-[#00D2EE]/30">
                      <Users className="h-4 w-4 stroke-[2.2]" />
                    </div>
                    <span className="text-[12px] sm:text-[13px] font-medium text-slate-200 text-left leading-tight whitespace-nowrap">
                      Lower service load
                    </span>
                  </div>
                </div>

                {/* Down Arrow with Dashed Line matching Screenshot 1 */}
                <div
                  onClick={handleScrollDown}
                  className="mt-10 sm:mt-12 flex flex-col items-center cursor-pointer group select-none"
                >
                  <svg
                    width="10"
                    height="90"
                    viewBox="0 0 10 90"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="opacity-75 transition-opacity group-hover:opacity-100"
                  >
                    <line
                      x1="5"
                      y1="0"
                      x2="5"
                      y2="80"
                      stroke="white"
                      strokeWidth="1.5"
                      strokeDasharray="3 3"
                    />
                    <path
                      d="M5 80L10 72M5 80L0 72"
                      stroke="white"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                  </svg>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* =========================================================================
            2. Case at a glance Section (matching Screenshot)
            Background: rgba(245, 250, 254, 1)
           ========================================================================= */}
        <section
          id="case-at-a-glance"
          className="w-full py-16 sm:py-20 lg:py-24 text-slate-900"
          style={{ backgroundColor: "rgba(245, 250, 254, 1)" }}
        >
          <div className="mx-auto max-w-[1440px] px-6 lg:px-[70px]">
            {/* Header row with horizontal divider line */}
            <div className="flex items-center gap-6 mb-8 sm:mb-10">
              <h2 className="text-[28px] sm:text-[34px] lg:text-[40px] font-bold tracking-tight bg-gradient-to-r from-[#00D2EE] to-[#2B7FFF] bg-clip-text text-transparent shrink-0">
                Case at a glance
              </h2>
              <div className="flex-1 h-[1px] bg-[#E2E8F0]" />
            </div>

            {/* Single Box with 2 Rows x 3 Cols (matching Screenshot) */}
            <div className="rounded-[16px] bg-white border border-[#E2EDF8] overflow-hidden shadow-sm divide-y divide-[#E2EDF8]">
              {/* Row 1: Industry, Key Users, Business Need */}
              <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-[#E2EDF8]">
                {CASE_ROW_1.map((item, idx) => {
                  const Icon = item.icon;
                  return (
                    <div key={idx} className="flex items-start gap-5 p-7 sm:p-8">
                      <div className="flex h-[54px] w-[54px] shrink-0 items-center justify-center rounded-full bg-[#EDF6FF] text-[#007AFF]">
                        <Icon className="h-6 w-6 stroke-[1.8]" />
                      </div>
                      <div>
                        <h3 className="text-[15px] sm:text-[16px] font-bold text-[#0F172A] leading-snug">
                          {item.title}
                        </h3>
                        <p className="mt-1.5 text-[13px] sm:text-[14px] leading-[22px] text-[#556376]">
                          {item.desc}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Row 2: Solution, Impact, Timeframe */}
              <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-[#E2EDF8]">
                {CASE_ROW_2.map((item, idx) => {
                  const Icon = item.icon;
                  return (
                    <div key={idx} className="flex items-start gap-5 p-7 sm:p-8">
                      <div className="flex h-[54px] w-[54px] shrink-0 items-center justify-center rounded-full bg-[#EDF6FF] text-[#007AFF]">
                        <Icon className="h-6 w-6 stroke-[1.8]" />
                      </div>
                      <div>
                        <h3 className="text-[15px] sm:text-[16px] font-bold text-[#0F172A] leading-snug">
                          {item.title}
                        </h3>
                        <p className="mt-1.5 text-[13px] sm:text-[14px] leading-[22px] text-[#556376]">
                          {item.desc}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* =========================================================================
            3. The Context Section (matching Screenshot 1 & 3)
            Background: #041421
            Right: Government Citizen Services desk with officer & citizen
            Left: The Context heading & narrative text
           ========================================================================= */}
        <section
          id="the-context"
          className="relative w-full overflow-hidden bg-[#041421] min-h-[320px] sm:min-h-[340px] lg:min-h-[350px] flex items-center text-white"
        >
          {/* Base Background Image: Flush to the VERY RIGHT of the section with zero margin/gap */}
          <div className="absolute right-0 top-0 bottom-0 h-full w-full sm:w-[58%] md:w-[54%] lg:w-[52%] xl:w-[50%] pointer-events-none select-none overflow-hidden">
            <img
              src="/images/insights/citizen-services-context.jpg"
              alt="Public Sector Citizen Services Context"
              className="h-full w-full object-cover object-right"
            />
            {/* Left progressive fade on the image to seamlessly blend into #041421 */}
            <div
              className="absolute inset-y-0 left-0 w-32 sm:w-44 lg:w-56 pointer-events-none"
              style={{
                background:
                  "linear-gradient(90deg, #041421 0%, rgba(4, 20, 33, 0.85) 35%, transparent 100%)",
              }}
            />
          </div>

          {/* Left Solid Dark Base to guarantee zero background bleed */}
          <div
            className="absolute inset-y-0 left-0 w-[46%] pointer-events-none bg-[#041421]"
          />

          <div className="relative z-10 mx-auto w-full max-w-[1440px] px-6 lg:px-[70px] py-10 sm:py-12 lg:py-0">
            <div className="max-w-[520px] sm:max-w-[580px] lg:max-w-[660px]">
              {/* Header row with horizontal divider line */}
              <div className="flex items-center gap-6 mb-5 sm:mb-6">
                <h2 className="text-[28px] sm:text-[34px] lg:text-[40px] font-bold tracking-tight bg-gradient-to-r from-[#00D2EE] to-[#2B7FFF] bg-clip-text text-transparent shrink-0">
                  The Context
                </h2>
                <div className="flex-1 h-[1px] bg-[#CEDDF1]/30" />
              </div>

              {/* Paragraph 1 */}
              <p className="text-[13px] sm:text-[14px] lg:text-[14.5px] leading-[22px] sm:leading-[24px] text-white/85">
                People turn to government and public agencies for answers: which form to use, which office to contact, what steps to follow and what documents to bring.
              </p>

              {/* Paragraph 2 */}
              <p className="mt-4 sm:mt-5 text-[13px] sm:text-[14px] lg:text-[14.5px] leading-[22px] sm:leading-[24px] text-white/85">
                When information is hard to find or hard to understand, citizens wait longer, call more often and visit counters that could have been avoided. A clear, single interface can guide citizens at the moment of need and free up staff for more complex cases.
              </p>
            </div>
          </div>
        </section>

        {/* =========================================================================
            4. What the solution does Section (matching Screenshot 1 & 2)
            Background: rgba(245, 250, 254, 1)
           ========================================================================= */}
        <section
          id="what-the-solution-does"
          className="w-full py-16 sm:py-20 lg:py-24 text-slate-900"
          style={{ backgroundColor: "rgba(245, 250, 254, 1)" }}
        >
          <div className="mx-auto max-w-[1440px] px-6 lg:px-[70px]">
            <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-12 justify-between">
              {/* Left Column: Solution Card matching Figma details (647px x 341px, Radius 12px, Shadow 4px 4px 24px rgba(0,0,0,0.25)) */}
              <div className="w-full lg:w-[647px] shrink-0 flex justify-center lg:justify-start">
                <div
                  style={{
                    boxShadow: "4px 4px 24px 0px rgba(0, 0, 0, 0.25)",
                    borderRadius: "12px",
                  }}
                  className="w-full max-w-[647px] h-[240px] sm:h-[300px] lg:h-[341px] overflow-hidden rounded-[12px] bg-white border border-[#E2EDF8]"
                >
                  <img
                    src="/images/insights/citizen-services-solution.jpg"
                    alt="Citizen Services Assistant Solution Interface"
                    className="w-full h-full object-cover object-center block select-none"
                  />
                </div>
              </div>

              {/* Right Column: Narrative Details */}
              <div className="flex-1 max-w-[560px] flex flex-col justify-center">
                {/* Header row with horizontal line matching Screenshot */}
                <div className="flex items-center gap-6 mb-6 sm:mb-7">
                  <h2 className="text-[28px] sm:text-[34px] lg:text-[40px] font-bold tracking-tight bg-gradient-to-r from-[#00D2EE] to-[#2B7FFF] bg-clip-text text-transparent shrink-0">
                    What the solution does
                  </h2>
                  <div className="flex-1 h-[1px] bg-[#E2E8F0]" />
                </div>

                <p className="text-[13.5px] sm:text-[14.5px] lg:text-[15px] leading-[24px] sm:leading-[26px] text-[#64748B]">
                  Citizens ask questions in everyday language. The Assistant searches approved public content — schemes, eligibility, process steps, forms and office information — and returns clear, cited answers.
                </p>

                <p className="mt-5 sm:mt-6 text-[13.5px] sm:text-[14.5px] lg:text-[15px] leading-[24px] sm:leading-[26px] text-[#64748B]">
                  It helps people understand what to do next and how to access the right service. Content stays under agency control, with privacy, role-based access and audit trails. The Assistant supports self-service and front-line staff, making the path clearer for routine queries.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* =========================================================================
            5. The challenge Section (matching Screenshot)
            Background: #FFFFFF (White)
            Left: The challenge heading + narrative paragraph
            Right: 2 Numbered Problem Cards (01, 02)
           ========================================================================= */}
        <section
          id="the-challenge"
          className="w-full py-16 sm:py-20 lg:py-24 bg-white text-slate-900"
        >
          <div className="mx-auto max-w-[1440px] px-6 lg:px-[70px]">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
              {/* Left Column: Heading + Narrative */}
              <div className="lg:col-span-5">
                <div className="flex items-center gap-6 mb-6 sm:mb-7">
                  <h2 className="text-[28px] sm:text-[34px] lg:text-[40px] font-bold tracking-tight bg-gradient-to-r from-[#00D2EE] to-[#2B7FFF] bg-clip-text text-transparent shrink-0">
                    The challenge
                  </h2>
                  <div className="flex-1 h-[1px] bg-[#E2E8F0]" />
                </div>

                <p className="text-[13.5px] sm:text-[14px] lg:text-[14.5px] leading-[23px] sm:leading-[25px] text-[#64748B]">
                  The challenge is to make a wide range of public information and services easy to find and understand, so citizens can complete more journeys on their own without needing repeated calls or visits.
                </p>
              </div>

              {/* Right Column: 2 Numbered Problem Cards */}
              <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6">
                {/* Card 01 */}
                <div className="rounded-[16px] bg-[#F5FAFE] border border-[#E2EDF8] p-6 sm:p-7 flex flex-col justify-between transition-shadow duration-200 hover:shadow-[0_4px_16px_rgba(15,23,42,0.04)]">
                  <div>
                    <span className="text-[26px] sm:text-[28px] font-bold text-[#007AFF] tracking-tight leading-none">
                      01
                    </span>
                    <h3 className="text-[16px] sm:text-[17px] font-bold text-[#0F172A] mt-3.5 mb-2 leading-snug">
                      Scattered information
                    </h3>
                    <p className="text-[13px] sm:text-[13.5px] leading-[21px] text-[#64748B]">
                      Schemes, processes and forms are spread across websites, offices and departments.
                    </p>
                  </div>
                </div>

                {/* Card 02 */}
                <div className="rounded-[16px] bg-[#F5FAFE] border border-[#E2EDF8] p-6 sm:p-7 flex flex-col justify-between transition-shadow duration-200 hover:shadow-[0_4px_16px_rgba(15,23,42,0.04)]">
                  <div>
                    <span className="text-[26px] sm:text-[28px] font-bold text-[#007AFF] tracking-tight leading-none">
                      02
                    </span>
                    <h3 className="text-[16px] sm:text-[17px] font-bold text-[#0F172A] mt-3.5 mb-2 leading-snug">
                      Process complexity
                    </h3>
                    <p className="text-[13px] sm:text-[13.5px] leading-[21px] text-[#64748B]">
                      Citizens often find it hard to understand eligibility, steps and required documents.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* =========================================================================
            6. Who benefits Section (matching Screenshot 1)
            Background: /images/insights/citizen-services-hero.jpg
            Table: Fill 1,280px, Radius 12px, rgba(9, 21, 40, 0.28), blur 16px
            Header: Height 49px, rgba(11, 59, 117, 0.65), borderBottom rgba(104, 104, 104, 0.75)
           ========================================================================= */}
        <section
          id="who-benefits"
          className="relative w-full overflow-hidden bg-[#020B18] py-16 sm:py-20 lg:py-24 text-white"
        >
          {/* Base Background Image: Hero Photo */}
          <div className="absolute inset-0 w-full h-full select-none pointer-events-none">
            <img
              src="/images/insights/citizen-services-hero.jpg"
              alt="Who Benefits Background"
              className="h-full w-full object-cover object-center"
            />
            {/* Dark glass overlay tint */}
            <div className="absolute inset-0 bg-[#020B18]/60 backdrop-blur-[2px]" />
          </div>

          <div className="relative z-10 mx-auto max-w-[1440px] px-6 lg:px-[70px]">
            {/* Header row with horizontal divider line */}
            <div className="flex items-center gap-6 mb-3 sm:mb-4">
              <h2 className="text-[28px] sm:text-[34px] lg:text-[40px] font-bold tracking-tight bg-gradient-to-r from-[#00D2EE] to-[#2B7FFF] bg-clip-text text-transparent shrink-0">
                Who benefits
              </h2>
              <div className="flex-1 h-[1px] bg-white/20" />
            </div>

            {/* Subtitle matching Screenshot */}
            <p className="text-[13.5px] sm:text-[14.5px] leading-[22px] sm:leading-[24px] text-white/80 mb-8 sm:mb-10 max-w-[1040px]">
              Five roles gain when public services are easier to navigate.
            </p>

            {/* Glassmorphic Table Container matching Figma Specs (Transparent Background) */}
            <div
              style={{
                backgroundColor: "rgba(9, 21, 40, 0.28)",
                backdropFilter: "blur(16px)",
                WebkitBackdropFilter: "blur(16px)",
                border: "1px solid rgba(104, 104, 104, 0.75)",
                boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.05)",
                borderRadius: "12px",
              }}
              className="w-full max-w-[1280px] mx-auto rounded-[12px] overflow-hidden"
            >
              {/* Table Header: Background rgba(11, 59, 117, 0.65), borderBottom 1px solid rgba(104, 104, 104, 0.75) */}
              <div
                style={{
                  backgroundColor: "rgba(11, 59, 117, 0.65)",
                  borderBottom: "1px solid rgba(104, 104, 104, 0.75)",
                }}
                className="grid grid-cols-12 px-6 h-[49px] items-center text-[11px] sm:text-[12px] font-bold tracking-[1.5px] uppercase text-white"
              >
                <div className="col-span-12 sm:col-span-4 lg:col-span-4">Role</div>
                <div className="hidden sm:block sm:col-span-8 lg:col-span-8">What improves for them</div>
              </div>

              {/* Table Rows with divider rgba(104, 104, 104, 0.75) */}
              <div
                className="divide-y"
                style={{
                  borderColor: "rgba(104, 104, 104, 0.75)",
                }}
              >
                {WHO_BENEFITS_DATA.map((item) => (
                  <div
                    key={item.role}
                    style={{ borderColor: "rgba(104, 104, 104, 1)" }}
                    className="grid grid-cols-12 px-6 py-3.5 sm:py-4 items-start gap-2 sm:gap-6 hover:bg-white/[0.04] transition-colors"
                  >
                    <div className="col-span-12 sm:col-span-4 lg:col-span-4 text-[13.5px] sm:text-[14px] lg:text-[14.5px] font-bold text-white leading-snug">
                      {item.role}
                    </div>
                    <div className="col-span-12 sm:col-span-8 lg:col-span-8 text-[13px] sm:text-[13.5px] leading-[21px] sm:leading-[22px] text-white/90">
                      <span className="sm:hidden text-[11px] uppercase tracking-wider text-slate-400 font-semibold block mb-1">
                        What improves:
                      </span>
                      {item.improves}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* =========================================================================
            7. Results Organizations Typically See Section
            Background: rgba(245, 250, 254, 1)
            6 Metric Cards (3 cols x 2 rows)
           ========================================================================= */}
        <section
          id="results-organizations-typically-see"
          className="w-full py-16 sm:py-20 lg:py-24 text-slate-900"
          style={{ backgroundColor: "rgba(245, 250, 254, 1)" }}
        >
          <div className="mx-auto max-w-[1440px] px-6 lg:px-[70px]">
            {/* Header row with horizontal divider line */}
            <div className="flex items-center gap-6 mb-8 sm:mb-10">
              <h2 className="text-[28px] sm:text-[34px] lg:text-[40px] font-bold tracking-tight bg-gradient-to-r from-[#00D2EE] to-[#2B7FFF] bg-clip-text text-transparent shrink-0">
                Results organizations typically see
              </h2>
              <div className="flex-1 h-[1px] bg-[#E2E8F0]" />
            </div>

            {/* 6 Cards Grid (3 cols x 2 rows matching other pages) */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
              {RESULTS_DATA.map((card, idx) => {
                const Icon = card.icon;
                return (
                  <div
                    key={idx}
                    className="flex items-start gap-4 sm:gap-5 rounded-[14px] sm:rounded-[16px] bg-white border border-[#EAF2FA] p-6 sm:p-7 shadow-[0_2px_12px_rgba(15,23,42,0.02)] transition-all duration-200 hover:shadow-[0_8px_24px_rgba(15,23,42,0.05)] hover:-translate-y-0.5"
                  >
                    {/* Circular Blue Icon Badge */}
                    <div className="flex h-13 w-13 sm:h-14 sm:w-14 shrink-0 items-center justify-center rounded-full bg-[#EBF5FF] text-[#007AFF]">
                      <Icon className="h-6 w-6 stroke-[2]" />
                    </div>

                    {/* Text Details */}
                    <div className="flex flex-col gap-1.5 pt-0.5">
                      <span className="text-[12.5px] sm:text-[13px] leading-[19px] text-[#64748B]">
                        {card.subtitle}
                      </span>
                      <h3 className="text-[15.5px] sm:text-[16.5px] font-bold text-[#0F172A] leading-snug">
                        {card.title}
                      </h3>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* =========================================================================
            8. How it works in practice Section (matching other pages)
            Background: #FFFFFF (White)
            5-Step Process Flow: Ask -> Find -> Guide -> Access -> Resolve
            Bottom: Head of Citizen Services Quote Box
           ========================================================================= */}
        <section
          id="how-it-works-in-practice"
          className="w-full py-16 sm:py-20 lg:py-24 bg-white text-slate-900"
        >
          <div className="mx-auto max-w-[1440px] px-6 lg:px-[70px]">
            {/* Header row with horizontal divider line */}
            <div className="flex items-center gap-6 mb-3 sm:mb-4">
              <h2 className="text-[28px] sm:text-[34px] lg:text-[40px] font-bold tracking-tight bg-gradient-to-r from-[#00D2EE] to-[#2B7FFF] bg-clip-text text-transparent shrink-0">
                How it works in practice
              </h2>
              <div className="flex-1 h-[1px] bg-[#E2E8F0]" />
            </div>

            {/* Subtitle */}
            <p className="text-[13.5px] sm:text-[14px] lg:text-[14.5px] leading-[23px] text-[#64748B] mb-12 sm:mb-16 max-w-[1040px]">
              From a citizen question to a resolved service journey — a simple, guided experience that works at scale.
            </p>

            {/* 5-Step Process Flow with Connectors */}
            <div className="flex flex-col lg:flex-row items-center justify-between gap-6 lg:gap-2">
              {PROCESS_STEPS_CITIZEN.map((step, idx) => {
                const Icon = step.icon;
                return (
                  <React.Fragment key={step.id}>
                    {/* Step Item */}
                    <div className="flex flex-col items-center text-center max-w-[210px] w-full group">
                      {/* Colored Circle Badge matching design system */}
                      <div
                        className={`flex h-16 w-16 sm:h-18 sm:w-18 items-center justify-center rounded-full ${step.circleClass} transition-transform duration-300 group-hover:scale-110 mb-4 sm:mb-5`}
                      >
                        <Icon className="h-7 w-7 sm:h-8 sm:w-8 stroke-[2.2]" />
                      </div>

                      {/* Step Title */}
                      <h3 className="text-[17px] sm:text-[18px] font-bold text-[#0F172A] mb-2">
                        {step.title}
                      </h3>

                      {/* Step Description */}
                      <p className="text-[12.5px] sm:text-[13px] leading-[19px] text-[#64748B]">
                        {step.desc}
                      </p>
                    </div>

                    {/* Connecting Arrow between steps (hidden on last step) */}
                    {idx < PROCESS_STEPS_CITIZEN.length - 1 && (
                      <div className="hidden lg:flex items-center justify-center shrink-0 px-2 text-[#00D2EE]">
                        <ArrowRight className="h-6 w-6 stroke-[2.5]" />
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>

            {/* Quote Callout Box */}
            <div className="mt-12 sm:mt-16 w-full rounded-[14px] sm:rounded-[18px] bg-[#F5FAFE] border border-[#E2EDF8] py-6 sm:py-7 px-6 sm:px-10 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6 shadow-[0_2px_12px_rgba(15,23,42,0.02)]">
              <div className="flex items-start gap-3.5">
                <span className="text-[#00D2EE] text-[32px] sm:text-[40px] font-serif leading-none select-none -mt-1">
                  &ldquo;
                </span>
                <p className="text-[14.5px] sm:text-[15.5px] font-medium text-[#1E293B] leading-[25px] sm:leading-[27px]">
                  &ldquo;The Assistant made it much easier for citizens to find the right information and complete their service journey without repeated calls or visits.&rdquo;
                </p>
              </div>

              <div className="shrink-0 lg:border-l-2 lg:border-[#00D2EE] lg:pl-6 pt-4 lg:pt-0 border-t lg:border-t-0 border-[#E2EDF8]">
                <span className="block text-[14px] sm:text-[14.5px] font-bold text-[#0F172A] leading-tight">
                  Head of Citizen Services
                </span>
                <span className="block text-[12px] sm:text-[12.5px] text-[#64748B] mt-1">
                  Public Sector Agency
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* =========================================================================
            9. CTA Banner Section (matching Screenshot 2 & 4)
            Background: /images/insights/citizen-services-cta-banner.jpg
           ========================================================================= */}
        <section
          id="cta-banner"
          className="relative w-full overflow-hidden bg-[#050B14] flex items-center min-h-[380px] lg:h-[380px] text-white"
        >
          {/* Base Background Image: Right-aligned focus */}
          <div className="absolute inset-0 w-full h-full pointer-events-none select-none overflow-hidden">
            <img
              src="/images/insights/citizen-services-cta-banner.jpg"
              alt="Make Public Services Simpler, Faster and More Accessible"
              className="h-full w-full object-cover object-right"
            />
          </div>

          {/* Subtle gradient overlay to make sure text is 100% readable on left side */}
          <div
            className="absolute inset-0 w-full h-full pointer-events-none"
            style={{
              background:
                "linear-gradient(90deg, #050B14 0%, rgba(5, 11, 20, 0.95) 42%, rgba(5, 11, 20, 0.45) 65%, transparent 100%)",
            }}
          />

          {/* Content Container */}
          <div className="relative z-10 mx-auto w-full max-w-[1440px] px-6 sm:px-10 lg:px-[70px] py-12 sm:py-16 lg:py-[80px] flex flex-col justify-center min-h-[380px]">
            <div className="max-w-[640px] w-full flex flex-col">
              {/* Heading matching Screenshot 4 */}
              <h2 className="text-[26px] sm:text-[32px] lg:text-[38px] font-bold tracking-tight leading-[1.2]">
                <span className="block text-white">
                  Make Public Services
                </span>
                <span className="block mt-1 bg-gradient-to-r from-[#00D2EE] to-[#2B7FFF] bg-clip-text text-transparent">
                  Simpler, Faster and More Accessible.
                </span>
              </h2>

              {/* Subtitle matching Screenshot 4 */}
              <p className="mt-3.5 sm:mt-4 text-[13.5px] sm:text-[14.5px] lg:text-[15px] leading-[22px] sm:leading-[24px] text-white/85 max-w-[540px]">
                See how Simpel Citizen Services Assistant can help your agency deliver clearer, more citizen-friendly services at scale.
              </p>

              {/* CTA Button matching Screenshot */}
              <div className="mt-6 sm:mt-8">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 rounded-full bg-white px-7 py-3 text-[14px] sm:text-[15px] font-semibold text-[#0F172A] shadow-md transition-all duration-200 hover:bg-slate-100 hover:shadow-lg hover:-translate-y-0.5"
                >
                  Request a Consultation
                  <ArrowRight className="h-4 w-4 stroke-[2.5] text-[#0F172A]" />
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default CitizenServicesAssistant;

